import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Alert, Box, Button, Card, CardContent, Grid, TextField, Typography, Link,
} from '@mui/material';
import { Science as ScienceIcon } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import usePageTitle from '../../hooks/usePageTitle';

export default function Register() {
  usePageTitle('Register');
  const navigate = useNavigate();
  const { register } = useAuth();
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', password: '', confirmPassword: '', organisationName: '' });
  const [error, setError] = useState(null);
  const [pendingMessage, setPendingMessage] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const setField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setPendingMessage(null);
    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    const result = await register(form.email, form.password, form.firstName, form.lastName, form.organisationName);
    setSubmitting(false);
    if (result.success) navigate('/');
    else if (result.pending) setPendingMessage(result.message);
    else setError(result.error);
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default' }}>
      <Card sx={{ width: '100%', maxWidth: 480, mx: 2 }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <ScienceIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
            <Typography variant="h5" fontWeight={700} color="primary.main">Create Account</Typography>
            <Typography variant="body2" color="text.secondary">Get started with Cleaning Validation Management</Typography>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {pendingMessage && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {pendingMessage} You will be notified once your organisation is approved.
            </Alert>
          )}

          {!pendingMessage && (
            <Box component="form" onSubmit={handleSubmit}>
              <TextField label="Organisation Name" fullWidth value={form.organisationName}
                onChange={e => setField('organisationName', e.target.value)}
                helperText="Leave blank to auto-generate from your name"
                sx={{ mb: 2 }} autoFocus />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField label="First Name" fullWidth required value={form.firstName}
                    onChange={e => setField('firstName', e.target.value)} />
                </Grid>
                <Grid item xs={6}>
                  <TextField label="Last Name" fullWidth required value={form.lastName}
                    onChange={e => setField('lastName', e.target.value)} />
                </Grid>
              </Grid>
              <TextField label="Email" type="email" fullWidth required value={form.email}
                onChange={e => setField('email', e.target.value)} sx={{ mt: 2 }} />
              <TextField label="Password" type="password" fullWidth required value={form.password}
                onChange={e => setField('password', e.target.value)} sx={{ mt: 2 }} />
              <TextField label="Confirm Password" type="password" fullWidth required value={form.confirmPassword}
                onChange={e => setField('confirmPassword', e.target.value)} sx={{ mt: 2, mb: 3 }} />
              <Button type="submit" variant="contained" fullWidth size="large" disabled={submitting}>
                {submitting ? 'Creating account...' : 'Register'}
              </Button>
            </Box>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
            Already have an account?{' '}
            <Link component={RouterLink} to="/login">Sign in</Link>
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}
